import { useEffect, useState } from "react";
import {
  CUSTOM_EVENT_INTRO_VIDEO,
  CUSTOM_EVENT_SESSION_ENDED,
  CUSTOM_EVENT_USER_JOINED,
  CUSTOM_EVENT_USER_LEFT,
} from "@icebergeradmin/common";
import { getLiveSessions } from "@/services/call-center/sessions.service";
import { TJoinedClient, TLiveSession } from "@/models/call.center.model";
import { useSocketContext } from "@/context/socket.context/socket.context";

export const useLiveSessions = (callCenterId?: number) => {
  const socket = useSocketContext();
  const [liveCalls, setLiveCalls] = useState<TLiveSession[]>([]);

  const joinedLeads: TJoinedClient[] = liveCalls.reduce((leads: TJoinedClient[], curr: TLiveSession) => {
    const callLead = curr.participants.find((i) => i.role === 0);
    if (callLead) {
      leads.push({
        ...callLead,
        leadId: callLead.leadId,
        sessionName: curr.sessionName,
        sessionStatus: curr.sessionStatus,
        startDate: curr.startDate,
        sessionId: curr.sessionId,
      });
    }
    return leads;
  }, []);

  const updateLiveCallsOnUserJoin = (user: TJoinedClient) => {
    const { name, email, role, sessionStatus, leadId, deviceType, phoneNumber } = user;
    setLiveCalls((prev) => {
      const existingCall = prev.find((call) => call.sessionName === user.sessionName);
      if (existingCall) {
        return prev.map((call) =>
          call.sessionName === user.sessionName
            ? {
              ...call,
              sessionId: call.sessionId,
              sessionStatus,
              startDate: user.startDate,
              participants: call.participants.some((p) => p.email === email)
                ? [...call.participants]
                : [...call.participants, { name, email, role, leadId, deviceType, phoneNumber }],
            }
            : call
        );
      }
      return [
        ...prev,
        {
          sessionId: user.sessionId,
          sessionName: user.sessionName,
          sessionStatus: user.sessionStatus,
          startDate: user.startDate,
          participants: [{ name, email, role, leadId, deviceType, phoneNumber }],
        },
      ];
    });
  };

  const loadLiveSessions = async () => {
    try {
      const response = await getLiveSessions(callCenterId);
      setLiveCalls(response);
    } catch {
      // Error loading live sessions
    }
  };

  useEffect(() => {
    if (!callCenterId) return;

    // Named handlers so cleanup can remove THESE specific listeners with
    // `socket.off(event, handler)`. The two-arg form is required: the
    // single-arg `socket.off(event)` wipes every listener for that event,
    // including those registered by other hooks (e.g. useNewLeadNotification
    // listens on USER_JOINED / INTRO_VIDEO too).
    const onUserJoined = (lead: TJoinedClient) => updateLiveCallsOnUserJoin(lead);
    const onIntroVideo = (lead: TJoinedClient) => updateLiveCallsOnUserJoin(lead);
    const onSessionEnded = ({ sessionName }: { sessionName: string }) => {
      setLiveCalls((prev) => prev.filter((call) => call.sessionName !== sessionName));
    };
    const onParticipantLeft = (
      { sessionName, email }: { sessionName: string; email: string },
    ) => {
      setLiveCalls((prev) =>
        prev
          .map((call) =>
            call.sessionName === sessionName
              ? {
                ...call,
                participants: call.participants.filter((p) => p.email !== email),
              }
              : call
          )
          .filter((call) => call.participants.length > 0),
      );
    };

    socket.on(CUSTOM_EVENT_USER_JOINED, onUserJoined);
    socket.on(CUSTOM_EVENT_INTRO_VIDEO, onIntroVideo);
    socket.on(CUSTOM_EVENT_SESSION_ENDED, onSessionEnded);
    socket.on(CUSTOM_EVENT_USER_LEFT, onParticipantLeft);

    loadLiveSessions();

    return () => {
      socket.off(CUSTOM_EVENT_USER_JOINED, onUserJoined);
      socket.off(CUSTOM_EVENT_INTRO_VIDEO, onIntroVideo);
      socket.off(CUSTOM_EVENT_SESSION_ENDED, onSessionEnded);
      socket.off(CUSTOM_EVENT_USER_LEFT, onParticipantLeft);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, callCenterId]);

  return {
    joinedLeads,
    liveCalls,
  };
};
